import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";
import { sar, monthLabel } from "./pcUtils";

const fill = (rows) => {
  if (!rows.length) return [];
  const map = Object.fromEntries(rows.map((r) => [r.month, r]));
  const out = [];
  let [y, m] = rows[0].month.split("-").map(Number);
  const [ey, em] = rows[rows.length - 1].month.split("-").map(Number);
  while (y < ey || (y === ey && m <= em)) {
    const k = `${y}-${String(m).padStart(2, "0")}`;
    out.push(map[k] || { month: k, sar: 0, usd: 0, count: 0 });
    m += 1; if (m > 12) { m = 1; y += 1; }
  }
  return out.map((r, i) => {
    const prev = i ? out[i - 1].sar : 0;
    return { ...r, label: `${monthLabel(r.month)} ${r.month.slice(2, 4)}`, change: prev ? Math.round(((r.sar - prev) / prev) * 100) : null };
  });
};

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  return (
    <div className="rounded-xl border border-violet-200 bg-white px-3 py-2 text-xs shadow-lg" dir="rtl">
      <p className="font-bold text-slate-800">{monthLabel(r.month)} {r.month.slice(0, 4)}</p>
      <p className="mt-1 text-violet-700">{sar(r.sar)}{r.usd ? ` · $${r.usd.toLocaleString("en-US")}` : ""}</p>
      <p className="text-slate-500">{r.count} فاتورة</p>
      {r.change !== null && <p className={r.change > 0 ? "text-rose-600" : "text-emerald-600"}>{r.change > 0 ? "▲" : "▼"} {Math.abs(r.change)}% عن الشهر السابق</p>}
    </div>
  );
};

export const EmergentMonthlyChart = ({ monthly = [] }) => {
  const data = fill(monthly);
  if (!data.length) return null;
  const max = Math.max(...data.map((r) => r.sar));
  const avg = data.reduce((a, r) => a + r.sar, 0) / data.length;
  const last = data[data.length - 1];
  const top = data.find((r) => r.sar === max);
  return (
    <div className="rounded-2xl border border-violet-200/70 bg-white p-5" data-testid="pc-emergent-monthly">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div><p className="text-sm font-bold text-slate-800">مصروفات Emergent شهرياً</p>
          <p className="text-[11px] text-slate-500">الخط المتقطع = متوسط الشهر ({sar(avg)})</p></div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className="rounded-full bg-violet-50 px-2.5 py-1 text-violet-700" data-testid="pc-emergent-peak">أعلى شهر: <b>{monthLabel(top.month)} {top.month.slice(0, 4)}</b> · {sar(top.sar)}</span>
          {last.change !== null && (
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${last.change > 0 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="pc-emergent-last-change">
              {last.change > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />} آخر شهر {last.change > 0 ? "+" : ""}{last.change}%</span>)}
        </div>
      </div>
      <div className="h-64" dir="ltr">
        <ResponsiveContainer>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={data.length > 14 ? 1 : 0} />
            <YAxis tick={{ fontSize: 10 }} width={45} />
            <Tooltip content={<Tip />} cursor={{ fill: "#f5f3ff" }} />
            <ReferenceLine y={avg} stroke="#a78bfa" strokeDasharray="5 4" />
            <Bar dataKey="sar" radius={[6, 6, 0, 0]}>
              {data.map((r) => <Cell key={r.month} fill={r.sar === max ? "#6d28d9" : r.sar > avg ? "#8b5cf6" : "#c4b5fd"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
